/**
 * The way app code talks to Sentry. Never import '@sentry/react' directly from code that can reach
 * the browser: a single static import puts the whole SDK (~160 KB gzip: core, tracing, replay) in
 * the bundle every page loads before it can respond, which is exactly what pages/+client.ts's lazy
 * init exists to avoid.
 *
 * Browser: calls queue until pages/+client.ts has loaded and initialised Sentry (after idle or the
 * first pointerdown) and called registerSentryModule(); then they replay in order. If Sentry never
 * loads (disabled in dev, init failed), the queue just stops growing at MAX_PENDING.
 * Server: reports go straight to the SDK (sentry-sdk.server-preload.ts, stubbed out of browser builds).
 */
import serverSentry from './sentry-sdk.server-preload';

type SentryModule = typeof import('@sentry/react');

const MAX_PENDING = 50;
let sentry: SentryModule | null = null;
const pending: Array<(s: SentryModule) => void> = [];

/** Called once by pages/+client.ts after Sentry.init; flushes everything queued so far. */
export function registerSentryModule(mod: SentryModule): void {
  sentry = mod;
  for (const fn of pending.splice(0)) {
    try {
      fn(mod);
    } catch {
      // A failing report must never break the app.
    }
  }
}

/** Runs `fn` with the Sentry SDK now if it's loaded, otherwise once it is. */
export function withSentry(fn: (s: SentryModule) => void): void {
  if (sentry) {
    try {
      fn(sentry);
    } catch {
      // A failing report must never break the app.
    }
    return;
  }
  if (typeof window === 'undefined') {
    const sdk = (serverSentry as { sdk?: SentryModule }).sdk;
    if (!sdk) return;
    try {
      fn(sdk);
    } catch {
      // A failing report must never break the app.
    }
    return;
  }
  if (pending.length < MAX_PENDING) pending.push(fn);
}

export function captureException(...args: Parameters<SentryModule['captureException']>): void {
  withSentry((s) => s.captureException(...args));
}

export function captureMessage(...args: Parameters<SentryModule['captureMessage']>): void {
  withSentry((s) => s.captureMessage(...args));
}

export function addBreadcrumb(...args: Parameters<SentryModule['addBreadcrumb']>): void {
  withSentry((s) => s.addBreadcrumb(...args));
}

export function setUser(...args: Parameters<SentryModule['setUser']>): void {
  withSentry((s) => s.setUser(...args));
}
